import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { RefreshCw, DollarSign, Zap, TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";

interface ModelUsage {
	requests: number;
	input_tokens: number;
	output_tokens: number;
	cached_tokens: number;
	timestamps: number[];
}

interface HourlyData {
	timestamp: number;
	timestamp_iso: string;
	total_requests: number;
	total_input_tokens: number;
	total_output_tokens: number;
	total_cached_tokens: number;
	by_model: Record<string, {
		requests: number;
		input_tokens: number;
		output_tokens: number;
		cached_tokens: number;
	}>;
}

interface ApiKeyUsage {
	requests: number;
	input_tokens: number;
	output_tokens: number;
	cached_tokens: number;
}

interface OpenAIUsageData {
	usage: {
		models: Record<string, ModelUsage>;
		api_keys: Record<string, ApiKeyUsage>;
		hourly: HourlyData[];
		raw_data: any[];
	};
	costs: {
		total: number;
		by_hour: Record<string, number>;
		currency: string;
		raw_data: any[];
	};
	metadata: {
		start_date: string;
		end_date: string;
		bucket_width: string;
	};
}

export const OpenAIUsage = () => {
	const [usageData, setUsageData] = useState<OpenAIUsageData | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split("T")[0]);
	const { toast } = useToast();

	const fetchUsageData = async () => {
		setIsLoading(true);
		try {
			const { data, error } = await supabase.functions.invoke(
				`get-openai-usage?start_date=${selectedDate}&end_date=${selectedDate}&bucket_width=1h`,
				{ method: 'GET' }
			);

			if (error)
				throw new Error(error.message || "Failed to fetch usage data");

			setUsageData(data);
		} catch (error: any) {
			toast({
				title: "Error fetching OpenAI usage",
				description: error.message,
				variant: "destructive",
			});
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		fetchUsageData();
	}, [selectedDate]);

	const goToPreviousDay = () => {
		const date = new Date(selectedDate);
		date.setDate(date.getDate() - 1);
		setSelectedDate(date.toISOString().split("T")[0]);
	};

	const goToNextDay = () => {
		const date = new Date(selectedDate);
		date.setDate(date.getDate() + 1);
		const today = new Date().toISOString().split("T")[0];
		const nextDate = date.toISOString().split("T")[0];
		if (nextDate <= today)
			setSelectedDate(nextDate);
	};

	const goToToday = () => {
		setSelectedDate(new Date().toISOString().split("T")[0]);
	};

	const isToday = selectedDate === new Date().toISOString().split("T")[0];

	if (isLoading)
		return (
			<div className="flex justify-center py-8">
				<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-digger-blue"></div>
			</div>
		);

	if (!usageData)
		return (
			<div className="text-center py-8">
				<p className="text-muted-foreground">No usage data available</p>
			</div>
		);

	const totalRequests = Object.values(usageData.usage.models).reduce((sum, m) => sum + m.requests, 0);
	const totalInputTokens = Object.values(usageData.usage.models).reduce((sum, m) => sum + m.input_tokens, 0);
	const totalOutputTokens = Object.values(usageData.usage.models).reduce((sum, m) => sum + m.output_tokens, 0);
	const totalCachedTokens = Object.values(usageData.usage.models).reduce((sum, m) => sum + m.cached_tokens, 0);

	// Prepare chart data for costs over time (hourly)
	const costChartData = Object.entries(usageData.costs.by_hour)
		.sort(([a], [b]) => a.localeCompare(b))
		.map(([hour, cost]) => {
			const date = new Date(hour);
			const formattedHour = `${String(date.getHours()).padStart(2, '0')}:00`;
			return { hour: formattedHour, cost };
		});

	const maxCost = Math.max(...costChartData.map(d => d.cost), 1);

	// Prepare hourly request data
	const hourlyRequestData = usageData.usage.hourly.map(hour => {
		const date = new Date(hour.timestamp_iso);
		const formattedHour = `${String(date.getHours()).padStart(2, '0')}:00`;
		return {
			hour: formattedHour,
			requests: hour.total_requests,
			input_tokens: hour.total_input_tokens / 1_000_000,
			output_tokens: hour.total_output_tokens / 1_000_000,
		};
	});

	const maxRequests = Math.max(...hourlyRequestData.map(d => d.requests), 1);

	return (
		<div className="space-y-6">
			<div className="flex justify-between items-center">
				<div className="flex gap-2 items-center">
					<Button onClick={goToPreviousDay} variant="outline" size="sm">
						Previous Day
					</Button>
					<div className="flex items-center gap-2">
						<label className="text-sm font-medium">Date:</label>
						<input
							type="date"
							value={selectedDate}
							onChange={(e) => setSelectedDate(e.target.value)}
							max={new Date().toISOString().split("T")[0]}
							className="px-2 py-1 border rounded"
						/>
					</div>
					<Button onClick={goToNextDay} variant="outline" size="sm" disabled={isToday}>
						Next Day
					</Button>
					<Button onClick={goToToday} variant="outline" size="sm" disabled={isToday}>
						Today
					</Button>
				</div>
				<Button onClick={fetchUsageData} variant="outline" size="sm">
					<RefreshCw className="mr-2 h-4 w-4" />
					Refresh
				</Button>
			</div>

			{/* Overview Cards */}
			<div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
				<Card>
					<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
						<CardTitle className="text-sm font-medium">Total Cost</CardTitle>
						<DollarSign className="h-4 w-4 text-muted-foreground" />
					</CardHeader>
					<CardContent>
						<div className="text-2xl font-bold">
							${usageData.costs.total.toFixed(2)}
						</div>
						<p className="text-xs text-muted-foreground">{usageData.costs.currency}</p>
					</CardContent>
				</Card>

				<Card>
					<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
						<CardTitle className="text-sm font-medium">Total Requests</CardTitle>
						<Zap className="h-4 w-4 text-muted-foreground" />
					</CardHeader>
					<CardContent>
						<div className="text-2xl font-bold">{totalRequests.toLocaleString()}</div>
						<p className="text-xs text-muted-foreground">API calls</p>
					</CardContent>
				</Card>

				<Card>
					<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
						<CardTitle className="text-sm font-medium">Input Tokens</CardTitle>
						<TrendingUp className="h-4 w-4 text-muted-foreground" />
					</CardHeader>
					<CardContent>
						<div className="text-2xl font-bold">{(totalInputTokens / 1_000_000).toFixed(2)}M</div>
						<p className="text-xs text-muted-foreground">
							{totalCachedTokens > 0 && `${(totalCachedTokens / 1_000_000).toFixed(2)}M cached`}
						</p>
					</CardContent>
				</Card>

				<Card>
					<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
						<CardTitle className="text-sm font-medium">Output Tokens</CardTitle>
						<TrendingUp className="h-4 w-4 text-muted-foreground" />
					</CardHeader>
					<CardContent>
						<div className="text-2xl font-bold">{(totalOutputTokens / 1_000_000).toFixed(2)}M</div>
						<p className="text-xs text-muted-foreground">Generated</p>
					</CardContent>
				</Card>
			</div>

			{/* Hourly Costs Chart */}
			<Card>
				<CardHeader>
					<CardTitle className="flex items-center">
						<DollarSign className="mr-2 h-5 w-5" />
						Hourly Costs
					</CardTitle>
					<CardDescription>
						Cost breakdown for {selectedDate}
					</CardDescription>
				</CardHeader>
				<CardContent>
					<div className="space-y-1 max-h-[500px] overflow-y-auto">
						{costChartData.length === 0 ? (
							<p className="text-sm text-muted-foreground">No cost data available for {selectedDate}</p>
						) : (
							costChartData.map(({ hour, cost }) => (
								<div key={hour} className="flex items-center gap-4">
									<span className="text-xs w-20 text-muted-foreground">{hour}</span>
									<div className="flex-1 bg-gray-200 rounded-sm h-5 relative">
										<div
											className="bg-green-600 h-5 rounded-sm flex items-center justify-end pr-2"
											style={{ width: `${(cost / maxCost) * 100}%` }}
										>
											<span className="text-xs text-white font-medium">
												${cost.toFixed(4)}
											</span>
										</div>
									</div>
								</div>
							))
						)}
					</div>
				</CardContent>
			</Card>

			{/* Hourly Requests Chart */}
			<Card>
				<CardHeader>
					<CardTitle className="flex items-center">
						<Zap className="mr-2 h-5 w-5" />
						Hourly Requests
					</CardTitle>
					<CardDescription>
						API request volume for {selectedDate}
					</CardDescription>
				</CardHeader>
				<CardContent>
					<div className="space-y-1 max-h-[500px] overflow-y-auto">
						{hourlyRequestData.length === 0 ? (
							<p className="text-sm text-muted-foreground">No request data available for {selectedDate}</p>
						) : (
							hourlyRequestData.map(({ hour, requests }) => (
								<div key={hour} className="flex items-center gap-4">
									<span className="text-xs w-20 text-muted-foreground">{hour}</span>
									<div className="flex-1 bg-gray-200 rounded-sm h-5 relative">
										<div
											className="bg-blue-500 h-5 rounded-sm flex items-center justify-end pr-2"
											style={{ width: `${(requests / maxRequests) * 100}%` }}
										>
											<span className="text-xs text-white font-medium">
												{requests} req
											</span>
										</div>
									</div>
								</div>
							))
						)}
					</div>
				</CardContent>
			</Card>

			{/* Usage by Model */}
			<Card>
				<CardHeader>
					<CardTitle className="flex items-center">
						<Zap className="mr-2 h-5 w-5" />
						Usage by Model
					</CardTitle>
					<CardDescription>Token consumption breakdown per model</CardDescription>
				</CardHeader>
				<CardContent>
					<div className="overflow-x-auto">
						<table className="w-full text-sm">
							<thead className="border-b">
								<tr>
									<th className="text-left py-2 px-2 font-medium">Model</th>
									<th className="text-right py-2 px-2 font-medium">Requests</th>
									<th className="text-right py-2 px-2 font-medium">Input Tokens</th>
									<th className="text-right py-2 px-2 font-medium">Output Tokens</th>
									<th className="text-right py-2 px-2 font-medium">Cached</th>
								</tr>
							</thead>
							<tbody>
								{Object.entries(usageData.usage.models)
									.sort(([, a], [, b]) => b.requests - a.requests)
									.map(([model, usage]) => (
										<tr key={model} className="border-b last:border-0">
											<td className="py-2 px-2 font-medium">{model}</td>
											<td className="text-right py-2 px-2">{usage.requests.toLocaleString()}</td>
											<td className="text-right py-2 px-2">
												{(usage.input_tokens / 1_000_000).toFixed(2)}M
											</td>
											<td className="text-right py-2 px-2">
												{(usage.output_tokens / 1_000_000).toFixed(2)}M
											</td>
											<td className="text-right py-2 px-2">
												{(usage.cached_tokens / 1_000_000).toFixed(2)}M
											</td>
										</tr>
									))}
							</tbody>
						</table>
					</div>
				</CardContent>
			</Card>

			{/* Usage by API Key */}
			<Card>
				<CardHeader>
					<CardTitle className="flex items-center">
						<TrendingUp className="mr-2 h-5 w-5" />
						Usage by API Key
					</CardTitle>
					<CardDescription>Token consumption breakdown per API key</CardDescription>
				</CardHeader>
				<CardContent>
					<div className="overflow-x-auto">
						<table className="w-full text-sm">
							<thead className="border-b">
								<tr>
									<th className="text-left py-2 px-2 font-medium">API Key ID</th>
									<th className="text-right py-2 px-2 font-medium">Requests</th>
									<th className="text-right py-2 px-2 font-medium">Input Tokens</th>
									<th className="text-right py-2 px-2 font-medium">Output Tokens</th>
									<th className="text-right py-2 px-2 font-medium">Cached</th>
								</tr>
							</thead>
							<tbody>
								{Object.entries(usageData.usage.api_keys)
									.sort(([, a], [, b]) => b.requests - a.requests)
									.map(([apiKey, usage]) => (
										<tr key={apiKey} className="border-b last:border-0">
											<td className="py-2 px-2 font-medium">{apiKey}</td>
											<td className="text-right py-2 px-2">{usage.requests.toLocaleString()}</td>
											<td className="text-right py-2 px-2">
												{(usage.input_tokens / 1_000_000).toFixed(2)}M
											</td>
											<td className="text-right py-2 px-2">
												{(usage.output_tokens / 1_000_000).toFixed(2)}M
											</td>
											<td className="text-right py-2 px-2">
												{(usage.cached_tokens / 1_000_000).toFixed(2)}M
											</td>
										</tr>
									))}
							</tbody>
						</table>
					</div>
				</CardContent>
			</Card>
		</div>
	);
};
